import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Truck, RotateCcw, Lock, Heart, Star, ArrowRight, Sparkles, Baby, Gift, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ProductCard from '../components/ProductCard';
import CategoryCard from '../components/CategoryCard';
import { products, categories, reviews, getBestsellers } from '../data/products';

const Home = () => {
  const [email, setEmail] = useState('');
  const bestsellers = getBestsellers();

  const trustFeatures = [
    { icon: ShieldCheck, title: 'Safe & Non-Toxic', description: 'All toys meet US safety standards' },
    { icon: Truck, title: 'Fast US Shipping', description: 'Free shipping over $50' },
    { icon: RotateCcw, title: '30-Day Returns', description: 'Hassle-free return policy' },
    { icon: Lock, title: 'Secure Checkout', description: '100% secure payment' },
    { icon: Heart, title: 'Family Trusted', description: 'Loved by thousands' },
  ];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for subscribing! Check your email for 10% off your first order.');
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-sky-50 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-400/20 rounded-full blur-xl" />
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-orange-400/20 rounded-full blur-xl" />
        <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-sky-400/20 rounded-full blur-xl" />

        <div className="max-w-7xl mx-auto px-4 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Hero Content */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm mb-6">
                <Sparkles className="text-yellow-500" size={18} />
                <span className="text-sm font-medium text-slate-600" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                  Trusted by 50,000+ Parents
                </span>
              </div>
              
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-6 leading-tight"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Fun, Safe & <span className="text-sky-500">Educational</span> Toys for Kids
              </h1>
              
              <p 
                className="text-lg text-slate-600 mb-8 max-w-lg mx-auto md:mx-0"
                style={{ fontFamily: 'Quicksand, sans-serif' }}
              >
                Discover toys that spark creativity, learning, and imagination. Perfect for ages 1-12.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start mb-8">
                <Link to="/category/all">
                  <Button 
                    className="rounded-full px-8 py-6 bg-orange-400 hover:bg-orange-500 text-white font-bold text-lg shadow-[0px_4px_0px_0px_#cc8400] hover:shadow-[0px_2px_0px_0px_#cc8400] active:shadow-none transition-all"
                    data-testid="shop-now-btn"
                  >
                    Shop Now
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/category/all">
                  <Button 
                    variant="outline"
                    className="rounded-full px-8 py-6 border-2 border-sky-500 text-sky-500 hover:bg-sky-50 font-bold text-lg"
                    data-testid="explore-toys-btn"
                  >
                    Explore Toys
                  </Button>
                </Link>
              </div>

              {/* Hero Highlights */}
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                {['Safe & Kid-Friendly', 'Fast US Shipping', 'Easy Returns'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-600">
                    <div className="w-2 h-2 bg-sky-500 rounded-full" />
                    <span className="text-sm font-medium" style={{ fontFamily: 'Quicksand, sans-serif' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=600&h=500&fit=crop"
                  alt="Happy kids playing with toys"
                  className="w-full h-auto"
                />
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Star className="text-yellow-500 fill-yellow-500" size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">4.9/5 Rating</p>
                    <p className="text-sm text-slate-500">10,000+ Reviews</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white border-y border-slate-100 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-12">
            {trustFeatures.slice(0, 4).map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <feature.icon className="text-sky-500" size={24} />
                <span className="text-slate-700 font-medium" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                  {feature.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              Shop by Category
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto" style={{ fontFamily: 'Quicksand, sans-serif' }}>
              Find the perfect toy for every age and interest
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.slice(0, 8).map((category) => (
              <CategoryCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="py-16 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div>
              <h2 
                className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Best Selling Toys
              </h2>
              <p className="text-slate-600" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                Our most loved toys by kids and parents
              </p>
            </div>
            <Link to="/category/all">
              <Button 
                variant="outline"
                className="rounded-full border-sky-500 text-sky-500 hover:bg-sky-100 mt-4 md:mt-0"
                data-testid="view-all-products-btn"
              >
                View All Products
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {bestsellers.slice(0, 8).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Parents Trust Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              Why Parents Trust Us
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto" style={{ fontFamily: 'Quicksand, sans-serif' }}>
              We're committed to providing the best toys for your children
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {trustFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="bg-sky-50 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="text-white" size={28} />
                </div>
                <h3 
                  className="font-bold text-slate-800 mb-2"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  {feature.title}
                </h3>
                <p 
                  className="text-sm text-slate-600"
                  style={{ fontFamily: 'Quicksand, sans-serif' }}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              What Parents Are Saying
            </h2>
            <p className="text-slate-600" style={{ fontFamily: 'Quicksand, sans-serif' }}>
              Real reviews from real families
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {reviews.slice(0, 3).map((review) => (
              <div 
                key={review.id}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={20} />
                  ))}
                </div>
                <p 
                  className="text-slate-700 mb-4"
                  style={{ fontFamily: 'Quicksand, sans-serif' }}
                >
                  "{review.text}"
                </p>
                <div className="flex items-center gap-3">
                  <img 
                    src={review.avatar} 
                    alt={review.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-slate-800">{review.name}</p>
                    <p className="text-sm text-slate-500">{review.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-sky-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
            <Gift className="text-white" size={18} />
            <span className="text-white font-medium">Special Offer</span>
          </div>
          
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            Get 10% Off Your First Order
          </h2>
          <p 
            className="text-sky-100 mb-8 max-w-lg mx-auto"
            style={{ fontFamily: 'Quicksand, sans-serif' }}
          >
            Subscribe to our newsletter for exclusive deals, new arrivals, and parenting tips!
          </p>

          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 rounded-full py-6 px-6 bg-white border-0"
              required
              data-testid="newsletter-email-input"
            />
            <Button 
              type="submit"
              className="rounded-full px-8 py-6 bg-orange-400 hover:bg-orange-500 text-white font-bold shadow-[0px_4px_0px_0px_#cc8400]"
              data-testid="newsletter-submit-btn"
            >
              Subscribe
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
